import React, { useEffect, useState } from 'react';
import { Box, Divider, IconButton, InputAdornment, Paper, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridLocaleText } from '@mui/x-data-grid';
import { koKR, enUS, jaJP } from '@mui/x-data-grid/locales';
import { getEmployeesAPI, getUsersAPI } from 'apis/settings/settingsAPI';
import EmployeePopup from 'components/menu/UserManagementPopup/EmployeePopup';
import { getPolicyAPI } from '../../apis/policy/policyAPI';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

interface Employee {
  id: number;
  username: string;
  name: string;
  email: string;
  notificationType?: string;
  notificationUpdater?: string;
  groupName?: string;
}

const locales: { [key: string]: Partial<GridLocaleText> } = {
  ko: koKR.components.MuiDataGrid.defaultProps.localeText,
  en: enUS.components.MuiDataGrid.defaultProps.localeText,
  ja: jaJP.components.MuiDataGrid.defaultProps.localeText
};

export default function EmployeeManagement() {
  const [employees, setEmployees] = useState<Employee[]>([]); // 직원 상태
  const [openEmployeePopup, setOpenEmployeePopup] = useState(false);
  // 클릭한 임직원 정보
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);

  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 다크 라이트 모드
  const theme = useTheme();

  // 임직원 팝업 열고 닫는 함수
  const handleOpenEmployeePopup = (employeeId: number) => {
    const employeeData = employees?.find((employee) => employee.id === employeeId) || null;
    setSelectedEmployee(employeeData); // employeeData가 undefined일 경우 null로 설정
    setOpenEmployeePopup(true);
  };
  const handleCloseEmployeePopup = async () => {
    setSelectedEmployee(null); // 선택된 사용자 초기화
    setOpenEmployeePopup(false);

    await handleRefresh();
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    const fetchUsers = async () => {
      try {
        const response = await getEmployeesAPI(); // API 호출
        // 데이터를 있으면 외부,내부 직원 데이터를 업데이트
        if (response) {
          setEmployees(response || []); // 직원 데이터 저장
        }
      } catch (error) {
        // console.error('사용자 데이터 가져오기 실패:', error);
      }
    };

    fetchUsers(); // 컴포넌트 마운트 시 데이터 가져오기

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // 빈 배열로 의존성 배열을 설정하여 처음 한 번만 실행

  const handleRefresh = async () => {
    try {
      const response = await getEmployeesAPI(); // API 호출
      // 데이터를 있으면 외부,내부 직원 데이터를 업데이트
      if (response) {
        setEmployees(response || []); // 직원 데이터 저장
      }
    } catch (error) {
      // console.error('사용자 데이터 가져오기 실패:', error);
    }
  };

  useEffect(() => {
    setFilteredEmployees(
      employees?.filter(
        (employee) => employee.username.toLowerCase().includes(filterText.toLowerCase()) || employee.name.toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }, [filterText, employees]);

  const columns: GridColDef[] = [
    { field: 'username', headerName: t('employee_notification.chart.id'), flex: 1 },
    { field: 'name', headerName: t('employee_notification.chart.name'), flex: 1 },
    { field: 'email', headerName: t('employee_notification.chart.email'), flex: 1.5 },
    {
      field: 'notificationType',
      headerName: t('employee_notification.chart.notification'),
      flex: 1,
      valueGetter: (params) => (params === 'EMAIL' ? t('employee_notification.chart.email') : t('employee_notification.chart.phone'))
    },
    { field: 'notificationUpdater', headerName: t('employee_notification.chart.notification_editor'), flex: 1 }
  ];

  return (
    <Box padding={2} display="flex" flexDirection="column" gap={2}>
      <Box
        // padding={2}
        // paddingBottom={2}
        borderRadius={1}
        // bgcolor={theme.palette.mode === 'dark' ? '#333' : '#f8f8f8'}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Typography variant="h5" fontWeight="bold">
          {t('employee_notification.title')}
        </Typography>

        <Divider flexItem />
      </Box>

      {/* 알박임직원 테이블 */}
      <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight="bold">
            {t('employee_notification.ulvac_employee')}
          </Typography>
          <TextField
            value={filterText}
            size="small"
            onChange={handleFilterChange}
            placeholder={t('employee_notification.search_placeholder')}
            sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'none' : '#ffffff', height: 29, width: 150 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ marginLeft: -1, marginRight: '-12px' }} />
                  </InputAdornment>
                )
              }
            }}
          />
        </Box>

        <Paper sx={{ height: 'calc(100vh - 346px)' }}>
          <DataGrid
            density="compact"
            columnHeaderHeight={60}
            rows={filteredEmployees}
            columns={columns}
            getRowId={(row) => row.id}
            disableColumnMenu
            disableColumnSelector
            disableRowSelectionOnClick
            showCellVerticalBorder
            onRowClick={(params) => handleOpenEmployeePopup(params.id as number)}
            localeText={locales[language]}
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.table.header
              },
              '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.table.border
              },
              '& .MuiDataGrid-cell': {
                cursor: 'pointer',
                borderRightColor: theme.palette.table.border,
                backgroundColor: theme.palette.table.row
              }
            }}
          />
        </Paper>
      </Box>

      {/* 임직원 클릭시 뜨는  Popup */}
      {/* 팝업 컴포넌트 호출 시 user 데이터 전달 */}
      {openEmployeePopup && (
        <EmployeePopup
          isOpen={openEmployeePopup}
          handleClose={handleCloseEmployeePopup}
          employeeData={selectedEmployee} // 선택된 사용자 데이터 전달
        />
      )}
    </Box>
  );
}
